/**
 * Pantalla de Rentabilidad: los dos rankings de margen que hasta ahora solo se
 * veian por CLI.
 *
 * Endpoints (existentes, de lectura, con datos de `ProfitabilityService`):
 *   GET /api/v1/pricing/top-profitable?limit=      -> get_top_profitable_products
 *   GET /api/v1/pricing/low-margin?min_margin=     -> get_low_margin_products
 *
 * ADVERTENCIA 1, QUE LA UI MUESTRA A PROPOSITO: ninguno de los dos recibe
 * `store_id`. Estan listados en `src/api/deps.py::UNSCOPED_LIST_ENDPOINTS`, o
 * sea que devuelven productos de TODAS las tiendas. El selector de arriba no
 * los filtra.
 *
 * ADVERTENCIA 2, QUE ES LA IMPORTANTE HOY: los dos rankings ordenan por
 * `(precio - costo) / precio`, y en produccion `products.cost` vale **0 en los
 * 661 productos** (verificado el 2026-08-27). Con costo cero esa cuenta da
 * exactamente 100% para todos, asi que:
 *
 *   * "mas rentables" es un empate de 661 filas y el orden que se ve es el que
 *     devolvio Postgres, no un ranking;
 *   * "margen bajo" **siempre viene vacio**, para cualquier umbral, porque
 *     ningun producto puede estar por debajo con margen 100%.
 *
 * La pantalla detecta las dos cosas EN VIVO --mirando los costos que devuelve
 * el propio endpoint, no una constante escrita aca-- y lo dice arriba de la
 * tabla. Mostrar una columna "Margen" con 100,0% repetido 20 veces sin decir de
 * donde sale es fabricar una conclusion falsa: la de que el catalogo tiene una
 * rentabilidad extraordinaria.
 *
 * El dia que se carguen los costos, los carteles desaparecen solos y el ranking
 * pasa a ser un ranking. No hay nada que volver a tocar aca.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import { getLowMarginProducts, getTopProfitableProducts } from "../api/endpoints";
import type { ProductProfitability } from "../api/types";
import { useAuth } from "../auth/AuthProvider";
import { Async } from "../components/AsyncBoundary";
import { useAsync } from "../components/useAsync";

type View = "top" | "low";

/** Opciones del tamanio del ranking. El backend no impone tope: 100 es nuestro. */
const TOP_LIMITS: readonly number[] = [10, 20, 50, 100];

/** Umbrales de margen "bajo" ofrecidos. `min_margin` es un float libre en la API. */
const MIN_MARGINS: readonly number[] = [5, 10, 15, 20, 30];

const currency = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  maximumFractionDigits: 0,
});

const controlStyle = {
  font: "inherit",
  padding: "0.35rem 0.5rem",
  borderRadius: "var(--radius)",
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
} as const;

const toolbarStyle = {
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
  flexWrap: "wrap",
  margin: "0 0 1rem",
} as const;

/**
 * Aviso de "estos margenes son un artefacto del costo cero".
 *
 * Se decide con las filas que ya llegaron, no con una constante: si todas
 * traen `cost === 0`, el margen que muestra la tabla es `(precio - 0) / precio`
 * y vale 100% por construccion. Si algunas tienen costo, se avisa que la
 * comparacion mezcla peras con manzanas, porque las de costo 0 van a copar la
 * cabeza del ranking.
 */
function CostArtifactNotice({ rows }: { readonly rows: readonly ProductProfitability[] }) {
  if (rows.length === 0) return null;

  const withoutCost = rows.filter((row) => row.cost === 0).length;
  if (withoutCost === 0) return null;

  if (withoutCost === rows.length) {
    return (
      <p className="screen__warning" role="note">
        <strong>Ninguno de estos productos tiene costo cargado.</strong> El margen se
        calcula como <code>(precio - costo) / precio</code>, asi que con{" "}
        <code>costo = 0</code> da 100% para todos y la columna "Ganancia" es simplemente
        el precio. El orden de las filas no es un ranking: es un empate desempatado por la
        base. Va a volverse informacion util recien cuando <code>products.cost</code> deje
        de estar en cero.
      </p>
    );
  }

  return (
    <p className="screen__warning" role="note">
      <strong>
        {withoutCost} de {rows.length} productos no tienen costo cargado.
      </strong>{" "}
      Esos aparecen con margen 100% por construccion (<code>(precio - 0) / precio</code>) y
      copan la cabeza del ranking, tapando a los que si tienen costo real.
    </p>
  );
}

function ProfitabilityTable({ rows }: { readonly rows: readonly ProductProfitability[] }) {
  return (
    // El contenedor scrollea por su cuenta: la tabla es ancha y el body de la
    // pagina no puede scrollear en horizontal.
    <div className="table-scroll">
      <table className="table">
        <thead>
          <tr>
            <th scope="col">SKU</th>
            <th scope="col">Producto</th>
            <th scope="col" className="table__num">
              Precio
            </th>
            <th scope="col" className="table__num">
              Costo
            </th>
            <th scope="col" className="table__num">
              Ganancia
            </th>
            <th scope="col" className="table__num">
              Margen
            </th>
            <th scope="col" className="table__num">
              Vendidos
            </th>
            <th scope="col">Accion</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <td>
                <code>{row.sku}</code>
              </td>
              <td>{row.title}</td>
              <td className="table__num">{currency.format(row.price)}</td>
              <td className="table__num">{currency.format(row.cost)}</td>
              <td className="table__num">{currency.format(row.profit)}</td>
              <td className="table__num">{row.margin.toFixed(1)}%</td>
              <td className="table__num">{row.sold_quantity.toLocaleString("es-AR")}</td>
              <td>
                {/* Lleva a Pricing, que resuelve el id con GET /products/{id} y
                    por lo tanto aplica tenencia. Como este listado no esta
                    acotado por tienda, un producto de otra tienda termina alli
                    en "no encontrado" en vez de mostrar datos ajenos. */}
                <Link to={`/pricing?product=${row.id}`}>Ver pricing</Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ProfitabilityScreen() {
  const { api } = useAuth();
  const [view, setView] = useState<View>("top");
  const [topLimit, setTopLimit] = useState<number>(20);
  const [minMargin, setMinMargin] = useState<number>(10);

  const { state, reload } = useAsync<ProductProfitability[]>(
    (signal) =>
      view === "top"
        ? getTopProfitableProducts(api, topLimit, { signal })
        : getLowMarginProducts(api, minMargin, { signal }),
    [api, view, topLimit, minMargin],
  );

  return (
    <section className="screen">
      <header className="screen__header">
        <h2>Rentabilidad</h2>
        <p className="screen__warning" role="note">
          Estos dos endpoints todavia no reciben <code>store_id</code>: los datos son de{" "}
          <strong>todas las tiendas</strong>, no solo de la seleccionada.
        </p>
      </header>

      <div className="tabs" role="tablist" aria-label="Vista de rentabilidad">
        <button
          type="button"
          role="tab"
          aria-selected={view === "top"}
          className={view === "top" ? "tab tab--active" : "tab"}
          onClick={() => setView("top")}
        >
          Mas rentables
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "low"}
          className={view === "low" ? "tab tab--active" : "tab"}
          onClick={() => setView("low")}
        >
          Margen bajo
        </button>
      </div>

      {view === "top" ? (
        <div style={toolbarStyle}>
          <label htmlFor="top-limit">Cuantos productos</label>
          <select
            id="top-limit"
            value={topLimit}
            onChange={(event) => setTopLimit(Number(event.target.value))}
            style={controlStyle}
          >
            {TOP_LIMITS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div style={toolbarStyle}>
          <label htmlFor="min-margin">Margen por debajo de</label>
          <select
            id="min-margin"
            value={minMargin}
            onChange={(event) => setMinMargin(Number(event.target.value))}
            style={controlStyle}
          >
            {MIN_MARGINS.map((value) => (
              <option key={value} value={value}>
                {value}%
              </option>
            ))}
          </select>
          <span className="feedback">
            El backend recorre todos los productos activos y filtra en memoria: no esta
            paginado.
          </span>
        </div>
      )}

      <Async
        state={state}
        onRetry={reload}
        loadingLabel="Calculando rentabilidad..."
        empty={
          view === "low" ? (
            <>
              Ningun producto activo tiene margen por debajo del {minMargin}%. Ojo con
              festejarlo: mientras <code>products.cost</code> siga en 0 el margen de todos
              da 100%, asi que esta lista va a estar vacia para cualquier umbral. Es un
              vacio que no significa "todo bien", significa "no hay costos cargados".
            </>
          ) : (
            <>No hay productos activos para rankear.</>
          )
        }
      >
        {(rows) => (
          <>
            <CostArtifactNotice rows={rows} />
            <ProfitabilityTable rows={rows} />
          </>
        )}
      </Async>
    </section>
  );
}
